import React, { useEffect } from 'react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import './IntroAnimation.css';

gsap.registerPlugin(SplitText);

interface IntroAnimationOverlayProps {
  onComplete?: () => void;
}

/**
 * Optimized IntroAnimation following Web Vitals best practices
 * - Hero content is pre-rendered in DOM (hidden) for early LCP paint
 * - Only animates opacity and transform (no layout shifts)
 * - Fixed overlay that fades out without resizing elements
 * - Aspect ratio reserved for all hero elements (zero CLS)
 */
const IntroAnimationOverlay: React.FC<IntroAnimationOverlayProps> = ({ onComplete }) => {
  useEffect(() => {
    // Add class to body to trigger CSS that hides header background
    document.body.classList.add('intro-animating');
    
    const runAnimation = async () => {
      // Mark animation as loaded immediately so CSS doesn't hide elements
      document.body.classList.add('animation-loaded');
      
      // Wait for fonts
      try {
        const anyDoc: any = document as any;
        const fonts: any = anyDoc?.fonts;
        if (fonts && fonts.status !== 'loaded') {
          await Promise.race([
            fonts.ready,
            new Promise<void>((resolve) => setTimeout(resolve, 1000))
          ]);
        }
      } catch {}

      const logoWrapper = document.getElementById('logoWrapper');
      const logoText = document.getElementById('logo');
      const overlay = document.getElementById('intro-overlay');
      
      if (!logoWrapper || !logoText || !overlay) return;

      const split = new SplitText(logoText, { type: "chars" });
      
      // Calculate center position for logo start
      const isMobile = window.innerWidth < 768;
      const rect = logoWrapper.getBoundingClientRect();
      const centerX = window.innerWidth / 2;
      const centerY = isMobile ? window.innerHeight / 3 : window.innerHeight / 2;
      
      // Calculate offset to center the logo
      const currentX = rect.left + rect.width / 2;
      const currentY = rect.top + rect.height / 2;
      const deltaX = centerX - currentX;
      const deltaY = centerY - currentY;
      
      // Start scale - how much bigger the logo should be
      const startScale = isMobile ? 3.5 : 4.5;
      
      // Set initial states - ONLY opacity and transform (no width/height changes)
      gsap.set(logoWrapper, { 
        x: deltaX,
        y: deltaY,
        opacity: 0,
        visibility: "visible",
        scale: startScale,
        transformOrigin: "center center"
      });
      gsap.set(split.chars, { y: '100%', display: 'inline-block' });
      gsap.set(overlay, { opacity: 1 });
      
      // Set initial states for header elements
      const headerLinks = document.querySelectorAll('.header-link');
      const buttons = document.querySelectorAll('.stagger-button');
      gsap.set([...headerLinks, ...buttons], { y: 20, opacity: 0, visibility: 'visible' });
      
      // Set initial states for hero content
      const heroTitle = document.querySelector('.hero-title');
      const heroSubtitle = document.querySelector('.hero-subtitle');
      const heroTitleLine = document.querySelector('.hero-title-line');
      const heroButtons = document.querySelectorAll('.hero-get-started');
      
      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      // Ensure title is always accessible to screen readers and read mode browsers
      if (heroTitle) {
        (heroTitle as HTMLElement).setAttribute('aria-hidden', 'false');
        (heroTitle as HTMLElement).style.visibility = 'visible';
        (heroTitle as HTMLElement).style.opacity = '1';
      }
      
      // Split title for better animation
      let heroTitleLineInners: HTMLElement[] = [];
      if (heroTitle && !prefersReducedMotion) {
        const heroTitleSplit = new SplitText(heroTitle, { type: 'lines' });
        gsap.set(heroTitle, { opacity: 1, visibility: 'visible' });
        
        // Mark as processed by intro animation to prevent double splitting
        (heroTitle as any)._splitTextProcessed = true;
        
        // Ensure all split lines are accessible
        (heroTitleSplit.lines as HTMLElement[]).forEach((line: HTMLElement) => {
          line.setAttribute('aria-hidden', 'false');
          line.style.visibility = 'visible';
        });
        
        // Create inner wrappers for masking effect
        (heroTitleSplit.lines as HTMLElement[]).forEach((line: HTMLElement) => {
          line.style.overflow = 'hidden';
          line.style.marginTop = '-0.1em';
          line.style.marginBottom = '-0.1em';
          
          const inner = document.createElement('span');
          inner.style.display = 'inline-block';
          inner.style.willChange = 'transform';
          // Ensure inner spans are accessible
          inner.setAttribute('aria-hidden', 'false');
          
          while (line.firstChild) {
            inner.appendChild(line.firstChild);
          }
          line.appendChild(inner);
          heroTitleLineInners.push(inner);
        });
        
        // Animate the inner spans
        gsap.set(heroTitleLineInners, { yPercent: 100 });
      }
      
      gsap.set([heroSubtitle, heroTitleLine], {
        y: 30,
        opacity: 0,
        visibility: 'visible'
      });
      gsap.set(heroButtons, { y: 30, opacity: 0, visibility: 'visible' });

      // GSAP Timeline - ONLY opacity and transform animations (zero CLS)
      const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
      
      // 1. Fade in logo (large, centered)
      tl.to(logoWrapper, {
        opacity: 1,
        duration: 0.75,
        ease: "power4.in"
      });
      
      // 2. Scale down and move to header position
      tl.to(logoWrapper, {
        x: 0,
        y: 0,
        scale: 1,
        duration: 0.6,
        ease: "power4.in"
      }, '+=0.01');
      
      // 3. Fade out overlay - ONLY opacity animation
      tl.to(overlay, {
        opacity: 0,
        duration: 0.6,
        ease: "power4.in"
      }, '<');
      
      // 4. Reveal logo text and header elements
      tl.to(split.chars, {
        y: '0%',
        duration: 0.35,
        stagger: 0.05,
        ease: "power2.out"
      }, '-=0.2');
      
      // Animate header links and buttons
      tl.to([...headerLinks, ...buttons], {
        opacity: 1,
        y: 0,
        duration: 0.4,
        stagger: 0.05,
        ease: "power2.out"
      }, '-=0.2');
      
      // 5. Animate hero content - ONLY opacity and y transform
      // Title lines (matches the original timing the removed heroBooksBackground tween provided)
      if (heroTitleLineInners.length > 0) {
        tl.to(heroTitleLineInners, {
          yPercent: 0,
          duration: 0.7,
          stagger: 0.08,
          ease: "power3.out"
        }, '-=0.5');
      }

      tl.to([heroSubtitle, heroTitleLine], {
        y: 0,
        opacity: 1,
        duration: 0.5,
        stagger: 0.1,
        ease: "power2.out"
      }, '-=0.5');

      // Final tween — owns the cleanup that used to live on the worksWithAll tween.
      tl.to(heroButtons, {
        y: 0,
        opacity: 1,
        duration: 0.5,
        stagger: 0.1,
        ease: "power2.out",
        onComplete: () => {
          document.body.classList.add('animation-complete');
          document.body.classList.remove('intro-animating');
          overlay.remove();
          onComplete?.();
        }
      }, '-=0.3');
    };

    // Small delay for DOM ready
    const timeoutId = setTimeout(runAnimation, 50);
    return () => {
      clearTimeout(timeoutId);
      document.body.classList.remove('intro-animating');
    };
  }, [onComplete]);

  return (
    <div 
      id="intro-overlay"
      className="fixed inset-0 z-[999] pointer-events-none"
      style={{
        background: 'var(--WhiteBrand-br-93)',
        opacity: 1
      }}
    />
  );
};

export default IntroAnimationOverlay;

